import React, {useState, useEffect} from 'react';
import {
    CreditCard,
    User,
    Mail,
    Map,
    MapPin,
    Globe,
    Phone,
    CheckCircle,
    XCircle,
    Loader2,
    AlertTriangle,
    Package,
    DollarSign,
    ChevronDown,
    ChevronUp,
    CheckSquare,
    RefreshCw,
    ListFilter
} from 'lucide-react';

const SubscriptionPage = () => {
    const [subscriptionData, setSubscriptionData] = useState({
        email: '',
        plan_name: '',
        subscription_status: '',
        address: '',
        city: '',
        state: '',
        country: '',
        phone_number: '',
    });

    const [isLoading, setIsLoading] = useState(false);
    const [userSubscriptions, setUserSubscriptions] = useState([]);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterPlan, setFilterPlan] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [showPlanFeatures, setShowPlanFeatures] = useState(true);

    // useEffect(() => {
    //     fetchUserSubscriptions();
    // }, []);

    const fetchUserSubscriptions = async () => {
        setIsLoadingUsers(true);
        setError(null);

        try {
            const accessToken = localStorage.getItem('accessToken');

            // Note: This endpoint is mocked since the original code doesn't show it
            // Replace with your actual endpoint
            const response = await fetch('https://api.digitalaalim.com/admin/user-subscriptions/', {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch user subscriptions: ${response.statusText}`);
            }

            const data = await response.json();
            setUserSubscriptions(data);
        } catch (error) {
            console.error('Error fetching user subscriptions:', error);
            setError(error.message);
        } finally {
            setIsLoadingUsers(false);
        }
    };

    const handleInputChange = (event) => {
        const {name, value} = event.target;
        setSubscriptionData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const updateSubscription = async (event) => {
        event.preventDefault();

        const confirmed = window.confirm("Are you sure you want to update this user's subscription?");
        if (!confirmed) return;

        setIsLoading(true);
        setError(null);

        try {
            const accessToken = localStorage.getItem('accessToken');

            const response = await fetch('https://api.digitalaalim.com/admin/set-subscription/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`,
                },
                body: JSON.stringify(subscriptionData),
            });

            if (!response.ok) {
                throw new Error(`Failed to update subscription: ${response.statusText}`);
            }

            const data = await response.json();

            alert(data.message || 'Subscription updated successfully');

            // Clear form
            setSubscriptionData({
                email: '',
                plan_name: '',
                subscription_status: '',
                address: '',
                city: '',
                state: '',
                country: '',
                phone_number: '',
            });

            // Refresh user subscriptions
            fetchUserSubscriptions();
        } catch (error) {
            console.error('Error updating subscription:', error);
            setError(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const fillFormWithUserData = (userData) => {
        setSubscriptionData({
            email: userData.email || '',
            plan_name: userData.plan_name || '',
            subscription_status: userData.subscription_status || '',
            address: userData.address || '',
            city: userData.city || '',
            state: userData.state || '',
            country: userData.country || '',
            phone_number: userData.phone_number || '',
        });

        // Scroll to top of the page
        window.scrollTo({top: 0, behavior: 'smooth'});
    };

    // Filter user subscriptions by search term, plan and status
    const filteredUsers = userSubscriptions.filter(user => {
        const matchesSearch =
            user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.country?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesPlan = filterPlan ? user.plan_name === filterPlan : true;
        const matchesStatus = filterStatus ? user.subscription_status === filterStatus : true;

        return matchesSearch && matchesPlan && matchesStatus;
    });

    // Get unique values for filters
    const plans = [...new Set(userSubscriptions.map(user => user.plan_name).filter(Boolean))];
    const statuses = [...new Set(userSubscriptions.map(user => user.subscription_status).filter(Boolean))];

    // Subscription plan features
    const planFeatures = {
        FREE: [
            '50 queries per month',
            'Basic support',
            'Standard response time',
            'Limited access to resources'
        ],
        STANDARD: [
            '500 queries per month',
            'Priority support',
            'Faster response time',
            'Full access to resources',
            'Advanced customization'
        ],
        PREMIUM: [
            'Unlimited queries',
            '24/7 premium support',
            'Instant response time',
            'Complete access to all resources',
            'Advanced customization and priority updates',
            'Dedicated account manager'
        ]
    };

    return (
        <div className="space-y-8">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
                    <CreditCard className="text-blue-600" size={32}/>
                    Subscription Management
                </h1>
                <p className="mt-2 text-gray-600">
                    Update user subscription plans and manage subscription details
                </p>
            </div>

            {/* Update Subscription Form */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="p-6 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-800">Update User Subscription</h2>
                    <p className="mt-1 text-sm text-gray-600">
                        Enter the user details and subscription information
                    </p>
                </div>

                <form onSubmit={updateSubscription} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Error message */}
                    {error && (
                        <div className="col-span-1 md:col-span-2 bg-red-50 border-l-4 border-red-500 p-4 rounded">
                            <div className="flex items-center">
                                <AlertTriangle size={20} className="text-red-500 mr-2 flex-shrink-0"/>
                                <p className="text-sm text-red-700">{error}</p>
                            </div>
                        </div>
                    )}

                    {/* Email */}
                    <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                            Email Address *
                        </label>
                        <div className="relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Mail size={16} className="text-gray-400"/>
                            </div>
                            <input
                                id="email"
                                type="email"
                                name="email"
                                value={subscriptionData.email}
                                onChange={handleInputChange}
                                required
                                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                placeholder="user@example.com"
                            />
                        </div>
                    </div>

                    {/* Plan */}
                    <div>
                        <label htmlFor="plan_name" className="block text-sm font-medium text-gray-700 mb-1">
                            Subscription Plan *
                        </label>
                        <div className="relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Package size={16} className="text-gray-400"/>
                            </div>
                            <select
                                id="plan_name"
                                name="plan_name"
                                value={subscriptionData.plan_name}
                                onChange={handleInputChange}
                                required
                                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            >
                                <option value="">Select Plan</option>
                                <option value="FREE">FREE</option>
                                <option value="STANDARD">STANDARD</option>
                                <option value="PREMIUM">PREMIUM</option>
                            </select>
                        </div>
                    </div>

                    {/* Status */}
                    <div>
                        <label htmlFor="subscription_status" className="block text-sm font-medium text-gray-700 mb-1">
                            Subscription Status *
                        </label>
                        <div className="relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <CheckSquare size={16} className="text-gray-400"/>
                            </div>
                            <select
                                id="subscription_status"
                                name="subscription_status"
                                value={subscriptionData.subscription_status}
                                onChange={handleInputChange}
                                required
                                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            >
                                <option value="">Select Status</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>
                        </div>
                    </div>

                    {/* Phone */}
                    <div>
                        <label htmlFor="phone_number" className="block text-sm font-medium text-gray-700 mb-1">
                            Phone Number *
                        </label>
                        <div className="relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Phone size={16} className="text-gray-400"/>
                            </div>
                            <input
                                id="phone_number"
                                type="text"
                                name="phone_number"
                                value={subscriptionData.phone_number}
                                onChange={handleInputChange}
                                required
                                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                placeholder="+1 (555) 123-4567"
                            />
                        </div>
                    </div>

                    {/* Address */}
                    <div className="col-span-1 md:col-span-2">
                        <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                            Address *
                        </label>
                        <div className="relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Map size={16} className="text-gray-400"/>
                            </div>
                            <input
                                id="address"
                                type="text"
                                name="address"
                                value={subscriptionData.address}
                                onChange={handleInputChange}
                                required
                                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                placeholder="123 Main St, Apt 4B"
                            />
                        </div>
                    </div>

                    {/* City */}
                    <div>
                        <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                            City *
                        </label>
                        <div className="relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <MapPin size={16} className="text-gray-400"/>
                            </div>
                            <input
                                id="city"
                                type="text"
                                name="city"
                                value={subscriptionData.city}
                                onChange={handleInputChange}
                                required
                                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                placeholder="New York"
                            />
                        </div>
                    </div>

                    {/* State */}
                    <div>
                        <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1">
                            State/Province *
                        </label>
                        <div className="relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <MapPin size={16} className="text-gray-400"/>
                            </div>
                            <input
                                id="state"
                                type="text"
                                name="state"
                                value={subscriptionData.state}
                                onChange={handleInputChange}
                                required
                                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                placeholder="NY"
                            />
                        </div>
                    </div>

                    {/* Country */}
                    <div>
                        <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
                            Country *
                        </label>
                        <div className="relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Globe size={16} className="text-gray-400"/>
                            </div>
                            <input
                                id="country"
                                type="text"
                                name="country"
                                value={subscriptionData.country}
                                onChange={handleInputChange}
                                required
                                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                placeholder="United States"
                            />
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="col-span-1 md:col-span-2 flex justify-end">
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 size={16} className="animate-spin mr-2"/>
                                    Updating...
                                </>
                            ) : (
                                <>
                                    <CreditCard size={16} className="mr-2"/>
                                    Update Subscription
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>

            {/* Subscription Plan Features */}
            {/*<div className="bg-white rounded-xl shadow-sm overflow-hidden">*/}
            {/*    <div*/}
            {/*        className="p-6 border-b border-gray-200 flex justify-between items-center cursor-pointer"*/}
            {/*        onClick={() => setShowPlanFeatures(!showPlanFeatures)}*/}
            {/*    >*/}
            {/*        <h2 className="text-xl font-semibold text-gray-800">Subscription Plan Features</h2>*/}
            {/*        <button className="text-gray-500">*/}
            {/*            {showPlanFeatures ? (*/}
            {/*                <ChevronUp size={20}/>*/}
            {/*            ) : (*/}
            {/*                <ChevronDown size={20}/>*/}
            {/*            )}*/}
            {/*        </button>*/}
            {/*    </div>*/}

            {/*    {showPlanFeatures && (*/}
            {/*        <div className="p-6">*/}
            {/*            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">*/}
            {/*                /!* Free Plan *!/*/}
            {/*                <div className="bg-gray-50 rounded-lg overflow-hidden border border-gray-200">*/}
            {/*                    <div className="p-6 bg-gray-100 border-b border-gray-200">*/}
            {/*                        <h3 className="text-lg font-semibold text-gray-800">Free Plan</h3>*/}
            {/*                        <div className="mt-4 flex items-baseline">*/}
            {/*                            <span className="text-3xl font-extrabold text-gray-900">$0</span>*/}
            {/*                            <span className="ml-1 text-xl font-semibold text-gray-500">/month</span>*/}
            {/*                        </div>*/}
            {/*                    </div>*/}
            {/*                    <div className="p-6">*/}
            {/*                        <ul className="space-y-4">*/}
            {/*                            {planFeatures.FREE.map((feature, index) => (*/}
            {/*                                <li key={index} className="flex items-start">*/}
            {/*                                    <CheckCircle size={16}*/}
            {/*                                                 className="text-green-500 mt-0.5 mr-2 flex-shrink-0"/>*/}
            {/*                                    <span className="text-sm text-gray-700">{feature}</span>*/}
            {/*                                </li>*/}
            {/*                            ))}*/}
            {/*                        </ul>*/}
            {/*                    </div>*/}
            {/*                </div>*/}

            {/*                /!* Standard Plan *!/*/}
            {/*                <div className="bg-blue-50 rounded-lg overflow-hidden border border-blue-200">*/}
            {/*                    <div className="p-6 bg-blue-100 border-b border-blue-200">*/}
            {/*                        <h3 className="text-lg font-semibold text-blue-800">Standard Plan</h3>*/}
            {/*                        <div className="mt-4 flex items-baseline">*/}
            {/*                            <span className="text-3xl font-extrabold text-blue-900">$9.99</span>*/}
            {/*                            <span className="ml-1 text-xl font-semibold text-blue-700">/month</span>*/}
            {/*                        </div>*/}
            {/*                    </div>*/}
            {/*                    <div className="p-6">*/}
            {/*                        <ul className="space-y-4">*/}
            {/*                            {planFeatures.STANDARD.map((feature, index) => (*/}
            {/*                                <li key={index} className="flex items-start">*/}
            {/*                                    <CheckCircle size={16}*/}
            {/*                                                 className="text-blue-500 mt-0.5 mr-2 flex-shrink-0"/>*/}
            {/*                                    <span className="text-sm text-blue-700">{feature}</span>*/}
            {/*                                </li>*/}
            {/*                            ))}*/}
            {/*                        </ul>*/}
            {/*                    </div>*/}
            {/*                </div>*/}

            {/*                /!* Premium Plan *!/*/}
            {/*                <div className="bg-purple-50 rounded-lg overflow-hidden border border-purple-200">*/}
            {/*                    <div className="p-6 bg-purple-100 border-b border-purple-200">*/}
            {/*                        <h3 className="text-lg font-semibold text-purple-800">Premium Plan</h3>*/}
            {/*                        <div className="mt-4 flex items-baseline">*/}
            {/*                            <span className="text-3xl font-extrabold text-purple-900">$29.99</span>*/}
            {/*                            <span className="ml-1 text-xl font-semibold text-purple-700">/month</span>*/}
            {/*                        </div>*/}
            {/*                    </div>*/}
            {/*                    <div className="p-6">*/}
            {/*                        <ul className="space-y-4">*/}
            {/*                            {planFeatures.PREMIUM.map((feature, index) => (*/}
            {/*                                <li key={index} className="flex items-start">*/}
            {/*                                    <CheckCircle size={16}*/}
            {/*                                                 className="text-purple-500 mt-0.5 mr-2 flex-shrink-0"/>*/}
            {/*                                    <span className="text-sm text-purple-700">{feature}</span>*/}
            {/*                                </li>*/}
            {/*                            ))}*/}
            {/*                        </ul>*/}
            {/*                    </div>*/}
            {/*                </div>*/}
            {/*            </div>*/}
            {/*        </div>*/}
            {/*    )}*/}
            {/*</div>*/}

            {/* User Subscriptions */}
            {/*<div className="bg-white rounded-xl shadow-sm overflow-hidden">*/}
            {/*    <div className="p-6 border-b border-gray-200">*/}
            {/*        <h2 className="text-xl font-semibold text-gray-800">User Subscriptions</h2>*/}
            {/*        <p className="mt-1 text-sm text-gray-600">*/}
            {/*            View and manage all user subscription details*/}
            {/*        </p>*/}
            {/*    </div>*/}

            {/*    /!* Filters and search *!/*/}
            {/*    <div className="p-4 border-b border-gray-200 bg-gray-50">*/}
            {/*        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">*/}
            {/*            <div className="w-full md:w-auto flex flex-col sm:flex-row gap-4">*/}
            {/*                <div className="relative w-full sm:w-64">*/}
            {/*                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">*/}
            {/*                        <User size={16} className="text-gray-400"/>*/}
            {/*                    </div>*/}
            {/*                    <input*/}
            {/*                        type="text"*/}
            {/*                        value={searchTerm}*/}
            {/*                        onChange={(e) => setSearchTerm(e.target.value)}*/}
            {/*                        placeholder="Search users..."*/}
            {/*                        className="pl-10 w-full border border-gray-300 rounded-md py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"*/}
            {/*                    />*/}
            {/*                </div>*/}

            {/*                <div className="inline-flex items-center">*/}
            {/*                    <ListFilter size={14} className="text-gray-500 mr-1"/>*/}
            {/*                    <select*/}
            {/*                        value={filterPlan}*/}
            {/*                        onChange={(e) => setFilterPlan(e.target.value)}*/}
            {/*                        className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"*/}
            {/*                    >*/}
            {/*                        <option value="">All Plans</option>*/}
            {/*                        {plans.map(plan => (*/}
            {/*                            <option key={plan} value={plan}>{plan}</option>*/}
            {/*                        ))}*/}
            {/*                    </select>*/}
            {/*                </div>*/}

            {/*                <div className="inline-flex items-center">*/}
            {/*                    <ListFilter size={14} className="text-gray-500 mr-1"/>*/}
            {/*                    <select*/}
            {/*                        value={filterStatus}*/}
            {/*                        onChange={(e) => setFilterStatus(e.target.value)}*/}
            {/*                        className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"*/}
            {/*                    >*/}
            {/*                        <option value="">All Status</option>*/}
            {/*                        {statuses.map(status => (*/}
            {/*                            <option key={status} value={status}>{status}</option>*/}
            {/*                        ))}*/}
            {/*                    </select>*/}
            {/*                </div>*/}
            {/*            </div>*/}

            {/*            <div className="flex items-center gap-2 w-full md:w-auto">*/}
            {/*                <button*/}
            {/*                    onClick={fetchUserSubscriptions}*/}
            {/*                    className="inline-flex items-center px-3 py-1 text-sm font-medium text-blue-700 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"*/}
            {/*                >*/}
            {/*                    <RefreshCw size={14} className="mr-1"/>*/}
            {/*                    Refresh*/}
            {/*                </button>*/}
            {/*            </div>*/}
            {/*        </div>*/}
            {/*    </div>*/}

            {/*    /!* User subscriptions list *!/*/}
            {/*    {isLoadingUsers ? (*/}
            {/*        <div className="flex justify-center items-center h-64">*/}
            {/*            <Loader2 size={40} className="animate-spin text-blue-600"/>*/}
            {/*        </div>*/}
            {/*    ) : error ? (*/}
            {/*        <div className="p-6">*/}
            {/*            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">*/}
            {/*                <div className="flex items-center">*/}
            {/*                    <AlertTriangle size={24} className="text-red-500 mr-3"/>*/}
            {/*                    <div>*/}
            {/*                        <h3 className="text-red-800 font-medium">Error loading user subscriptions</h3>*/}
            {/*                        <p className="text-red-700">{error}</p>*/}
            {/*                    </div>*/}
            {/*                </div>*/}
            {/*            </div>*/}
            {/*        </div>*/}
            {/*    ) : (*/}
            {/*        <div className="overflow-x-auto">*/}
            {/*            {filteredUsers.length > 0 ? (*/}
            {/*                <table className="min-w-full divide-y divide-gray-200">*/}
            {/*                    <thead className="bg-gray-50">*/}
            {/*                    <tr>*/}
            {/*                        <th scope="col"*/}
            {/*                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">*/}
            {/*                            User*/}
            {/*                        </th>*/}
            {/*                        <th scope="col"*/}
            {/*                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">*/}
            {/*                            Plan*/}
            {/*                        </th>*/}
            {/*                        <th scope="col"*/}
            {/*                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">*/}
            {/*                            Location*/}
            {/*                        </th>*/}
            {/*                        <th scope="col"*/}
            {/*                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">*/}
            {/*                            Status*/}
            {/*                        </th>*/}
            {/*                        <th scope="col"*/}
            {/*                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">*/}
            {/*                            Actions*/}
            {/*                        </th>*/}
            {/*                    </tr>*/}
            {/*                    </thead>*/}
            {/*                    <tbody className="bg-white divide-y divide-gray-200">*/}
            {/*                    {filteredUsers.map((user) => (*/}
            {/*                        <tr key={user.email} className="hover:bg-gray-50">*/}
            {/*                            <td className="px-6 py-4">*/}
            {/*                                <div className="flex items-center">*/}
            {/*                                    <div*/}
            {/*                                        className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">*/}
            {/*                <span className="text-blue-600 font-medium">*/}
            {/*                  {user.name ? user.name.charAt(0).toUpperCase() : 'U'}*/}
            {/*                </span>*/}
            {/*                                    </div>*/}
            {/*                                    <div className="ml-4">*/}
            {/*                                        <div className="text-sm font-medium text-gray-900">{user.name}</div>*/}
            {/*                                        <div className="text-sm text-gray-500">{user.email}</div>*/}
            {/*                                        <div className="text-xs text-gray-500">{user.phone_number}</div>*/}
            {/*                                    </div>*/}
            {/*                                </div>*/}
            {/*                            </td>*/}
            {/*                            <td className="px-6 py-4 whitespace-nowrap">*/}
            {/*                                <div*/}
            {/*                                    className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${*/}
            {/*                                        user.plan_name === 'PREMIUM'*/}
            {/*                                            ? 'bg-purple-100 text-purple-800'*/}
            {/*                                            : user.plan_name === 'STANDARD'*/}
            {/*                                                ? 'bg-blue-100 text-blue-800'*/}
            {/*                                                : 'bg-gray-100 text-gray-800'*/}
            {/*                                    }`}>*/}
            {/*                                    {user.plan_name || 'FREE'}*/}
            {/*                                </div>*/}
            {/*                            </td>*/}
            {/*                            <td className="px-6 py-4 whitespace-nowrap">*/}
            {/*                                <div className="text-sm text-gray-900">{user.city}, {user.state}</div>*/}
            {/*                                <div className="text-xs text-gray-500">{user.country}</div>*/}
            {/*                            </td>*/}
            {/*                            <td className="px-6 py-4 whitespace-nowrap">*/}
            {/*            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${*/}
            {/*                user.subscription_status === 'active'*/}
            {/*                    ? 'bg-green-100 text-green-800'*/}
            {/*                    : 'bg-red-100 text-red-800'*/}
            {/*            }`}>*/}
            {/*              {user.subscription_status || 'inactive'}*/}
            {/*            </span>*/}
            {/*                            </td>*/}
            {/*                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">*/}
            {/*                                <button*/}
            {/*                                    onClick={() => fillFormWithUserData(user)}*/}
            {/*                                    className="text-blue-600 hover:text-blue-900"*/}
            {/*                                >*/}
            {/*                                    Edit*/}
            {/*                                </button>*/}
            {/*                            </td>*/}
            {/*                        </tr>*/}
            {/*                    ))}*/}
            {/*                    </tbody>*/}
            {/*                </table>*/}
            {/*            ) : (*/}
            {/*                <div className="text-center py-12">*/}
            {/*                    {searchTerm || filterPlan || filterStatus ? (*/}
            {/*                        <div className="flex flex-col items-center">*/}
            {/*                            <CreditCard size={40} className="text-gray-400 mb-4"/>*/}
            {/*                            <h3 className="text-lg font-medium text-gray-800 mb-2">No subscriptions*/}
            {/*                                found</h3>*/}
            {/*                            <p className="text-gray-600 mb-4">*/}
            {/*                                No subscriptions match your current search filters*/}
            {/*                            </p>*/}
            {/*                            <button*/}
            {/*                                onClick={() => {*/}
            {/*                                    setSearchTerm('');*/}
            {/*                                    setFilterPlan('');*/}
            {/*                                    setFilterStatus('');*/}
            {/*                                }}*/}
            {/*                                className="text-blue-600 hover:text-blue-800 hover:underline"*/}
            {/*                            >*/}
            {/*                                Clear all filters*/}
            {/*                            </button>*/}
            {/*                        </div>*/}
            {/*                    ) : (*/}
            {/*                        <div className="flex flex-col items-center">*/}
            {/*                            <CreditCard size={40} className="text-gray-400 mb-4"/>*/}
            {/*                            <h3 className="text-lg font-medium text-gray-800 mb-2">No subscriptions*/}
            {/*                                available</h3>*/}
            {/*                            <p className="text-gray-600">*/}
            {/*                                There are no user subscriptions to display*/}
            {/*                            </p>*/}
            {/*                        </div>*/}
            {/*                    )}*/}
            {/*                </div>*/}
            {/*            )}*/}
            {/*        </div>*/}
            {/*    )}*/}
            {/*</div>*/}
        </div>
    );
};

export default SubscriptionPage;